import React, { useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@material-ui/core";
import {
  ActionButton,
  ExpandableCard,
  ExperiencesAvailabilityCard,
  Icon,
  IconName,
} from "halifax";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { ActivitiesEntryTypeEnum, CallState, ExperienceAvailability, RewardsAccount } from "redmond";
import { History } from "history";
import { PATH_ACTIVITIES_SHOP, getConfigTenant, getExperiencesShopQuery, isVesperTenant } from "@capone/common";
import clsx from "clsx";
import "./styles.scss";

const CARD_KEY = "activities-xsell";

interface IActivitiesXsellSectionProps {
  location: string;
  experiences: ExperienceAvailability[];
  callState: CallState;
  onCtaClick: () => void;
  rewardsAccount?: RewardsAccount;
  matchesMobile?: boolean;
  history: History;
  fromDate?: Date | string | null;
  untilDate?: Date | string | null;
  isTravelApp?: boolean;
  entryType?: ActivitiesEntryTypeEnum;
}

export const ActivitiesXsellSection = ({
  location,
  experiences,
  callState,
  onCtaClick,
  rewardsAccount,
  matchesMobile,
  history,
  fromDate,
  untilDate,
  isTravelApp,
  entryType = ActivitiesEntryTypeEnum.MYTRIPS_XSELL,
}: IActivitiesXsellSectionProps) => {
  const [expandedCardKey, setExpandedCardKey] = useState(CARD_KEY);

  const isLoading = callState === CallState.InProcess;
  const hasFailed = callState === CallState.Failed;

  const earnTagContent = useMemo(() => {
    if (!rewardsAccount) return undefined;
    const { earn, rewardsBalance } = rewardsAccount;
    const currency = rewardsBalance.currencyDescription ?? rewardsBalance.currency;
    const multiplier = currency.includes("cash") ? "%" : "X";
    return (
      <>
        <Icon name={IconName.StarIcon} />
        <Typography className="earn-tag-text">
          {"Earn "}
          <b>{earn.experiencesMultiplier}{multiplier} {currency}</b>
          {" on activities"}
        </Typography>
      </>
    );
  }, [rewardsAccount]);

  if (hasFailed || (!isLoading && experiences.length === 0)) {
    return null;
  }

  const skeletonExperiences: (ExperienceAvailability | undefined)[] = isLoading
    ? [undefined, undefined, undefined]
    : experiences;

  const isExpanded = expandedCardKey === CARD_KEY;

  return (
    <Box className={clsx("activities-xsell-wrapper", {
      mobile: matchesMobile,
      redesigned: isVesperTenant(getConfigTenant())
    })}>
      <ExpandableCard
        className="activities-xsell-section-root"
        isMobile={matchesMobile}
        cardKey={CARD_KEY}
        expandedCardKey={expandedCardKey}
        handleCardKeyChange={() =>
          setExpandedCardKey((prev) => (prev === CARD_KEY ? "" : CARD_KEY))
        }
        content={{
          title: (
            <Box className="activities-xsell-title-row">
              <Typography variant="h3" dangerouslySetInnerHTML={{
                __html: `<strong>Complete your trip</strong> with popular activities in ${location}`,
              }} />
              {!matchesMobile && (
                <ActionButton
                  className="activities-cross-sell-cta b2b"
                  message="Explore activities"
                  icon={<FontAwesomeIcon icon={faArrowRight} />}
                  iconPosition="right"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onCtaClick();
                  }}
                />
              )}
            </Box>
          ),
          body: (
            <Box className="activities-xsell-cards">
              {skeletonExperiences.map((experience, index) => (
                <ExperiencesAvailabilityCard
                  key={experience?.id?.value ?? index}
                  experiencesAvailabilityInfo={experience}
                  variant={matchesMobile ? "card" : "compact-row"}
                  isMobile={matchesMobile}
                  isSkeleton={isLoading}
                  earnTagContent={earnTagContent}
                  isInXSell={matchesMobile}
                  showCoverImageOnly={matchesMobile}
                  onClick={experience ? () => {
                    const shopUrl = `${PATH_ACTIVITIES_SHOP}${getExperiencesShopQuery(
                      experience.id,
                      fromDate ? new Date(fromDate) : null,
                      untilDate ? new Date(untilDate) : null,
                      location,
                      isTravelApp,
                      index,
                      entryType,
                      [entryType],
                    )}`;
                    if (matchesMobile) {
                      history.push(shopUrl);
                    } else {
                      window.open(shopUrl, "_blank");
                    }
                  } : undefined}
                />
              ))}
              {matchesMobile && !isLoading && (
                <Box
                  className="activities-xsell-mobile-cta"
                  onClick={onCtaClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && onCtaClick()}
                >
                  <Box className="activities-xsell-mobile-cta-circle">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Box>
                  <Typography className="activities-xsell-mobile-cta-label">
                    {`Explore\n${location}`}
                  </Typography>
                </Box>
              )}
            </Box>
          ),
        }}
      />
      <Box className="activities-xsell-toggle-row">
        <IconButton
          className="activities-xsell-toggle-btn"
          onClick={() =>
            setExpandedCardKey((prev) => (prev === CARD_KEY ? "" : CARD_KEY))
          }
          aria-label={isExpanded ? "Collapse activities section" : "Expand activities section"}
          size="small"
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </IconButton>
      </Box>
    </Box>
  );
};
